import { ShieldCheck } from 'lucide-react';

/* ───────────────── Vendor logo marks (inline SVG / brand-accurate) ───────────────── */

/* Microsoft official four-square */
export const MicrosoftLogo = ({ size = 16 }) => {
  const s = Math.floor(size / 2) - 1;
  return (
    <span style={{ display: 'inline-grid', gridTemplateColumns: `${s}px ${s}px`, gap: '2px', width: size, height: size, flexShrink: 0 }}>
      <span style={{ background: '#F25022' }} />
      <span style={{ background: '#7FBA00' }} />
      <span style={{ background: '#00A4EF' }} />
      <span style={{ background: '#FFB900' }} />
    </span>
  );
};

/* Azure — simplified A-mark in Azure cyan */
export const AzureLogo = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <defs>
      <linearGradient id="azGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%"  stopColor="#0078D4" />
        <stop offset="100%" stopColor="#005A9E" />
      </linearGradient>
    </defs>
    <path d="M9.105 4l-5.94 11.16h5.04L13.245 4h-4.14zm5.985 1.245L11.07 12.36l4.62 5.85L9.165 20H22.5l-7.41-14.755z" fill="url(#azGrad)" />
  </svg>
);

/* Google "G" — official 4-color */
export const GoogleLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18A10.97 10.97 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" />
  </svg>
);

/* Zoho — official favicon */
export const ZohoLogo = ({ size = 18 }) => (
  <img
    src="https://www.zohowebstatic.com/sites/zweb/images/favicon.ico"
    alt="Zoho"
    width={size}
    height={size}
    style={{ flexShrink: 0, objectFit: 'contain' }}
  />
);

/* React — official atom mark in React blue */
export const ReactLogo = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="-11.5 -10.23 23 20.46" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <circle cx="0" cy="0" r="2.05" fill="#61DAFB" />
    <g stroke="#61DAFB" strokeWidth="1" fill="none">
      <ellipse rx="11" ry="4.2" />
      <ellipse rx="11" ry="4.2" transform="rotate(60)" />
      <ellipse rx="11" ry="4.2" transform="rotate(120)" />
    </g>
  </svg>
);

/* Apple — official Apple logo silhouette */
export const AppleLogo = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path
      d="M17.05 12.04c-.03-2.65 2.16-3.93 2.26-3.99-1.23-1.8-3.15-2.05-3.83-2.08-1.63-.16-3.18.96-4.01.96-.83 0-2.1-.94-3.45-.92-1.78.03-3.42 1.03-4.33 2.62-1.85 3.21-.47 7.95 1.32 10.55.88 1.27 1.92 2.7 3.27 2.65 1.32-.05 1.81-.85 3.4-.85 1.59 0 2.04.85 3.43.82 1.42-.03 2.32-1.29 3.18-2.57.74-1.1 1.04-2.17 1.06-2.23-.02-.01-2.04-.78-2.06-3.1zM14.49 4.36c.73-.89 1.22-2.12 1.08-3.36-1.05.04-2.32.7-3.07 1.58-.67.79-1.26 2.04-1.1 3.25 1.16.09 2.36-.59 3.09-1.47z"
      fill="#000000"
    />
  </svg>
);

/* Android — official green robot head */
export const AndroidLogo = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path
      d="M17.523 8.97l1.16-2.01a.24.24 0 0 0-.087-.327.24.24 0 0 0-.327.087l-1.175 2.035A7.43 7.43 0 0 0 12 7.5c-1.85 0-3.553.617-4.094.954L6.731 6.42a.24.24 0 0 0-.327-.087.24.24 0 0 0-.087.327l1.16 2.01C5.512 10.04 4.5 12.07 4.5 14.4h15c0-2.33-1.012-4.36-2.977-5.43zM8.5 12.6a.7.7 0 1 1 0-1.4.7.7 0 0 1 0 1.4zm7 0a.7.7 0 1 1 0-1.4.7.7 0 0 1 0 1.4z"
      fill="#3DDC84"
    />
  </svg>
);

/* Interakt — green chat-bubble */
export const InteraktLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <rect width="24" height="24" rx="5" fill="#0FAE60" />
    <path d="M12 5C7.582 5 4 8.358 4 12.5c0 1.34.382 2.595 1.05 3.69L4 20l3.95-1.025A8.5 8.5 0 0 0 12 20c4.418 0 8-3.358 8-7.5S16.418 5 12 5z" fill="#fff" />
  </svg>
);

/* ───────────────── PartnerBadge component ───────────────── */

/**
 * Variants:
 *   "default"       white card, full size
 *   "compact"       smaller, denser
 *   "dark"          dark background variant
 *   "dark-compact"  dark + compact (footer)
 */
export const PartnerBadge = ({
  logo,
  vendor,
  certification,
  variant = 'default',
}) => {
  const isCompact = variant === 'compact' || variant === 'dark-compact';
  const isDark = variant === 'dark' || variant === 'dark-compact';

  if (isCompact) {
    return (
      <div className={`inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-md border ${
        isDark
          ? 'bg-white/5 border-white/10'
          : 'bg-white border-[#E3E8EE]'
      }`}>
        <span className="flex items-center justify-center w-5 h-5">{logo}</span>
        <div className="flex flex-col leading-tight">
          <span className={`text-[0.55rem] font-medium tracking-wide ${isDark ? 'text-white/60' : 'text-[#8898AA]'}`}>
            {vendor}
          </span>
          <span className={`text-[0.7rem] font-semibold ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
            {certification}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`group inline-flex items-stretch rounded-lg overflow-hidden border transition-all duration-300 ${
      isDark
        ? 'bg-white/[0.04] border-white/10 hover:bg-white/[0.06] hover:border-white/20'
        : 'bg-white border-[#E3E8EE] hover:border-[#0F172A]/15 hover:shadow-[0_4px_14px_rgba(15,23,42,0.08)]'
    }`}>
      <div className={`flex items-center justify-center w-11 px-3 ${
        isDark ? 'bg-white/[0.03]' : 'bg-[#F6F9FC]'
      }`}>
        {logo}
      </div>
      <div className="flex flex-col justify-center pl-3 pr-4 py-2">
        <span className={`text-[0.55rem] font-medium tracking-wide uppercase ${isDark ? 'text-white/55' : 'text-[#8898AA]'}`}>
          {vendor}
        </span>
        <span className={`text-[0.78rem] font-semibold leading-tight ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
          {certification}
        </span>
      </div>
      <div className={`flex items-center justify-center w-9 border-l ${
        isDark ? 'border-white/10' : 'border-[#E3E8EE]'
      }`} title="Verified Partner">
        <ShieldCheck size={14} className="text-[#2563EB]" />
      </div>
    </div>
  );
};

/* ───────────────── Pre-built convenience badges ───────────────── */

export const ShopifyBadge = ({ variant = 'default' }) => (
  <PartnerBadge
    variant={variant}
    logo={<img src="/resources/shopify.png" alt="Shopify" className="w-full h-auto object-contain" style={{ maxHeight: 18 }} />}
    vendor="Shopify"
    certification="Expert Partner"
  />
);

export const MicrosoftBadge = ({ variant = 'default' }) => (
  <PartnerBadge
    variant={variant}
    logo={<MicrosoftLogo size={16} />}
    vendor="Microsoft"
    certification="Solutions Partner"
  />
);

export const AzureBadge = ({ variant = 'default' }) => (
  <PartnerBadge
    variant={variant}
    logo={<AzureLogo size={18} />}
    vendor="Microsoft"
    certification="Azure Specialist"
  />
);

export const GoogleBadge = ({ variant = 'default' }) => (
  <PartnerBadge
    variant={variant}
    logo={<GoogleLogo size={16} />}
    vendor="Google"
    certification="Workspace Partner"
  />
);

export const ZohoBadge = ({ variant = 'default' }) => (
  <PartnerBadge
    variant={variant}
    logo={<ZohoLogo size={18} />}
    vendor="Zoho"
    certification="Authorized Partner"
  />
);

export const InteraktBadge = ({ variant = 'default' }) => (
  <PartnerBadge
    variant={variant}
    logo={<InteraktLogo size={16} />}
    vendor="Interakt"
    certification="WhatsApp Specialist"
  />
);

export const ReactBadge = ({ variant = 'default' }) => (
  <PartnerBadge
    variant={variant}
    logo={<ReactLogo size={18} />}
    vendor="React"
    certification="Web Development"
  />
);

export const AppleBadge = ({ variant = 'default' }) => (
  <PartnerBadge
    variant={variant}
    logo={<AppleLogo size={17} />}
    vendor="Apple"
    certification="iOS Development"
  />
);

export const AndroidBadge = ({ variant = 'default' }) => (
  <PartnerBadge
    variant={variant}
    logo={<AndroidLogo size={18} />}
    vendor="Google"
    certification="Android Development"
  />
);
